/**
 * Enhanced Markdown Renderer
 * Supports:
 * - **bold**
 * - *italic*
 * - <u>underline</u>
 * - # Large Heading
 * - ## Medium Heading
 * - Bullet lists (-, *, �)
 * - 1. Numbered lists
 */

const parseInline = (text: string): React.ReactNode[] => {
    const regex = /(\*\*.*?\*\*|\*.*?\*|<u>.*?<\/u>)/g;
    const parts = text.split(regex);

    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
            return <em key={i}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('<u>') && part.endsWith('</u>')) {
            return <u key={i}>{part.slice(3, -4)}</u>;
        }
        return part;
    });
};

const BULLET_PREFIX = /^(?:-|\*|�|•)\s+/;

export const renderMarkdown = (text: string | undefined): React.ReactNode => {
    if (!text) return null;

    const lines = text.split('\n');

    return (
        <>
            {lines.map((line, index) => {
                const normalizedLine = line.replace(/•/g, '�');
                const trimmed = normalizedLine.trim();

                if (trimmed.startsWith('# ')) {
                    return (
                        <div key={index} className="text-lg font-bold mt-2 mb-1">
                            {parseInline(trimmed.slice(2))}
                        </div>
                    );
                }
                if (trimmed.startsWith('## ')) {
                    return (
                        <div key={index} className="text-base font-bold mt-2 mb-1">
                            {parseInline(trimmed.slice(3))}
                        </div>
                    );
                }

                if (BULLET_PREFIX.test(trimmed)) {
                    return (
                        <div key={index} className="flex ml-2">
                            <span className="mr-2">�</span>
                            <span>{parseInline(trimmed.replace(BULLET_PREFIX, ''))}</span>
                        </div>
                    );
                }

                if (/^\d+\.\s/.test(trimmed)) {
                    const contentStart = trimmed.indexOf(' ') + 1;
                    return (
                        <div key={index} className="flex ml-2">
                            <span className="mr-2 font-mono">{trimmed.split(' ')[0]}</span>
                            <span>{parseInline(trimmed.slice(contentStart))}</span>
                        </div>
                    );
                }

                if (trimmed === '') {
                    return <div key={index} className="h-2" />;
                }

                return <div key={index}>{parseInline(normalizedLine)}</div>;
            })}
        </>
    );
};
